import { useCallback, useEffect, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import ContactActions from 'app/shared/flux/actions/ContactActions';
import UserActions from 'app/shared/flux/actions/UserActions';
import UserSearchActions from 'app/shared/flux/actions/UserSearchActions';
import FetchListingActions from 'app/shared/flux/actions/FetchListingActions';
import AppActions from 'app/shared/flux/actions/AppActions';
import ErrorActions from 'app/shared/flux/actions/ErrorActions';
import ConversationActions from 'app/shared/flux/actions/ConversationActions';
import NotificationActions from 'app/shared/flux/actions/NotificationActions';
import type { ListingDetails } from 'app/types/listingDetails.type';
import type { UserState } from 'app/shared/flux/reducers/user';
import contactUtils from 'app/shared/utils/contactUtils';
import numberUtils from 'app/shared/utils/numberUtils';
import loginUtils from 'app/shared/utils/loginUtils';
import routeUtils from 'app/shared/utils/routeUtils';
import { trackForRentContact } from 'app/client/tracking';
import {
  type ContactFormLeadTriggerObject,
  emitTrackInlineContactSubmitEvent,
  emitTrackModalContactSubmitEvent,
  type PhoneLeadTriggerObject,
} from 'app/shared/models/Clickstream/ContactClickstreamEvents';
import { getListingType, getTrackingFunction } from 'app/shared/utils/clickstreamUtils';
import { useModal } from 'app/shared/contexts/ModalContext/useModal';
import { getGlobalLogger } from '@zg-rentals/logger-base';
import { noop } from 'lodash';

const logger = getGlobalLogger();

export interface ContactFormData {
  userName: string;
  userEmail: string;
  userPhoneNumber: string;
  userMessage: string;
  lowIncomeMessage?: string | null;
  numBaths?: string;
  numBeds?: string;
  requestToApply?: boolean;
}

interface ValidationResult {
  data: {
    email: { valid: boolean };
    phone: { valid: boolean };
  };
}

interface UserDetailsResponse {
  error?: boolean;
  message?: string;
  status?: 'ACCOUNT_DEACTIVATED' | 'USER_ALREADY_EXISTS' | string;
  success?: boolean;
  data?: {
    passwordStatus?: string;
  };
}

interface DisplaySearchPromptInquiryResponse {
  data: boolean;
}

interface UseContactSubmissionOptions {
  listing: ListingDetails;
  user: UserState;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  dispatch: any; // Redux ThunkDispatch type (using any to avoid complex type gymnastics)
  onClose?: () => void;
  closePopup?: () => void;
  onHidePopup?: () => void;
  isPopup?: boolean;
  isLoggedIn?: boolean;
  hasShownPostContactSavedSearch?: boolean;
  customTracking?: () => void;
  countryCode?: string;
  pathname?: string;
  shouldUseAreaBoundary?: boolean;
  triggerLocation?: 'home_details' | 'search_results';
  triggerObject?: ContactFormLeadTriggerObject;
  unitDetails?: {
    modelId?: string;
    [key: string]: unknown;
  };
}

interface UseContactSubmissionReturn {
  handleSubmitForm: (inputValues: ContactFormData) => Promise<void>;
  handlePhoneClick: () => void;
  formState: {
    isLoading: boolean;
    contactSubmitted: boolean;
    errorMessage: string;
    emailErrorMessage: string;
    phoneErrorMessage: string;
    isSuccessPopupVisible: boolean;
  };
  clearErrors: () => void;
}

/**
 * Custom hook for managing contact form submission logic
 * Handles validation, user authentication, inquiry submission, and modal orchestration
 */
export const useContactSubmission = (options: UseContactSubmissionOptions): UseContactSubmissionReturn => {
  const {
    listing,
    user,
    dispatch,
    onClose,
    closePopup,
    onHidePopup,
    isPopup = false,
    isLoggedIn = false,
    hasShownPostContactSavedSearch = false,
    customTracking,
    countryCode,
    pathname,
    shouldUseAreaBoundary = true,
    triggerLocation = 'home_details',
    triggerObject,
    unitDetails = {},
  } = options;

  const { openModal } = useModal();

  const [formState, setFormState] = useState({
    isLoading: false,
    contactSubmitted: false,
    errorMessage: '',
    emailErrorMessage: '',
    phoneErrorMessage: '',
    isSuccessPopupVisible: false,
  });

  const [shouldDisplayPostContactSavedSearch, setShouldDisplayPostContactSavedSearch] = useState(false);

  // Fetch whether to show saved search prompt after contact submission
  useEffect(() => {
    const useMapBounds = pathname ? routeUtils.isAreaUrl(pathname) || routeUtils.isPadOrBuildingUrl(pathname) : false;

    let isMounted = true;
    (
      dispatch(
        UserSearchActions.search.saved.displaySearchPromptInquiry({ shouldUseAreaBoundary, useMapBounds }),
      ) as unknown as Promise<DisplaySearchPromptInquiryResponse>
    )
      .then((res) => {
        if (isMounted) {
          const { data } = res;
          setShouldDisplayPostContactSavedSearch(Boolean(data));
        }
        return res;
      })
      .catch((error: Error) => {
        if (isMounted) {
          dispatch(
            ErrorActions.errorHandler({
              error,
              errorLocation: 'hook.useContactSubmission.useEffect',
              errorClass: 'userSearchActions',
            }),
          );
        }
      });

    return () => {
      isMounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleFormSubmitFailure = useCallback((response: { message?: string } = {}) => {
    setFormState((prev) => ({
      ...prev,
      isLoading: false,
      errorMessage: response.message || 'Failed to send inquiry.',
    }));
    logger?.error(response, 'Form submit failure');
  }, []);

  const getInquiryParams = useCallback(
    (inputValues: ContactFormData) => {
      const { trusted, aliasEncoded } = listing;
      const demoted = listing.details?.demoted;
      const listingPaid = Boolean(trusted);
      const listingDemoted = Boolean(demoted);

      return {
        alias: aliasEncoded,
        displayName: listing.displayName,
        address: listing.address.street,
        baths: inputValues.numBaths,
        beds: inputValues.numBeds,
        email: inputValues.userEmail,
        phone: numberUtils.formatPhoneNumber(inputValues.userPhoneNumber),
        name: inputValues.userName,
        text: contactUtils.getInquiryText({
          userMessage: inputValues.userMessage,
          lowIncomeMessage: inputValues.lowIncomeMessage,
          listing,
        }),
        textToCache: inputValues.userMessage,
        emailUser: true,
        defaultMessage: listing.contact.defaultInquiryMessage,
        paid: listingPaid,
        demoted: listingDemoted,
        modelId: unitDetails.modelId ? unitDetails.modelId : null,
        requestToApply: inputValues.requestToApply,
      };
    },
    [listing, unitDetails],
  );

  const validateUserInfo = useCallback(
    async (inputValues: ContactFormData) => {
      const { aliasEncoded: listingAlias } = listing;
      const email = inputValues.userEmail;
      const phone = inputValues.userPhoneNumber;
      const params = { listingAlias, email, phone };

      // Reset state before proceeding
      setFormState((prev) => ({
        ...prev,
        emailErrorMessage: '',
        phoneErrorMessage: '',
        isLoading: true,
      }));

      // Call validation endpoint
      try {
        const res = (await dispatch(
          UserActions.inquiry.validateUserContactInfo(params),
        )) as unknown as ValidationResult;
        const data = res.data;
        const emailData = data.email;
        const phoneData = data.phone;

        // If errors are detected, let the UI know
        if (!emailData.valid) {
          setFormState((prev) => ({
            ...prev,
            emailErrorMessage: 'Invalid email address',
            isLoading: false,
          }));
        }

        if (!phoneData.valid) {
          setFormState((prev) => ({
            ...prev,
            phoneErrorMessage: 'Invalid phone number',
            isLoading: false,
          }));
        }

        return { isValid: emailData.valid && phoneData.valid };
      } catch (error) {
        logger?.error(error, 'Phone and/or email validation error');
        setFormState((prev) => ({
          ...prev,
          errorMessage: 'We could not validate your contact information. Please try again.',
          isLoading: false,
        }));
        return { isValid: false };
      }
    },
    [dispatch, listing],
  );

  const handleTrackSubmitClick = useCallback(
    (rentalSubmitId: string) => {
      const listingType = getListingType(listing);
      const leadType = 'submit';

      // Determine trigger object based on context
      const defaultTriggerObject: ContactFormLeadTriggerObject = isPopup
        ? 'home_details_component|modal_contact_form'
        : 'home_details_component|inline_form';
      const finalTriggerObject: ContactFormLeadTriggerObject = triggerObject || defaultTriggerObject;

      if (customTracking) {
        customTracking();
      } else if (isPopup) {
        emitTrackModalContactSubmitEvent({
          listing,
          rentalSubmitId,
          triggerLocation,
          triggerObject: finalTriggerObject,
          triggerSource: 'button_to_complete_contact_form',
        });
      } else {
        emitTrackInlineContactSubmitEvent({
          listing,
          rentalSubmitId,
          triggerLocation,
        });
      }

      if (isPopup) {
        getTrackingFunction({ type: listingType, leadType })?.({
          listing,
          triggerLocation,
          triggerObject: finalTriggerObject,
          triggerSource: 'button_to_complete_contact_form',
        });
      } else {
        getTrackingFunction({ type: listingType, leadType })?.({
          listing,
          triggerLocation,
          triggerObject: finalTriggerObject,
          triggerSource: 'button_to_complete_contact_form',
        });
      }
    },
    [listing, customTracking, isPopup, triggerLocation, triggerObject],
  );

  const continueContactInquiryFlow = useCallback(async () => {
    const { trusted, aliasEncoded } = listing;
    const { inquiryParams, rentalSubmitId } = contactUtils.getDelayedContactInquiry();

    // Ensure inquiryParams is not null before proceeding
    if (!inquiryParams) {
      logger?.error({ inquiryParams, rentalSubmitId, listingAlias: listing.aliasEncoded }, 'Missing inquiry params');
      handleFormSubmitFailure({ message: 'Something went wrong. Please try again later.' });
      return;
    }

    try {
      await dispatch(
        ContactActions.submitContact({
          listing: listing as unknown as Record<string, unknown>,
          inquiryParams,
          rentalSubmitId: rentalSubmitId ?? undefined,
        }),
      );

      trackForRentContact(listing);

      // Close the current modal first (check all possible close callbacks)
      if (onClose) {
        onClose();
      } else if (closePopup) {
        closePopup();
      } else if (onHidePopup) {
        onHidePopup();
      }

      // Update internal state
      setFormState((prev) => ({
        ...prev,
        isLoading: false,
        contactSubmitted: true,
        isSuccessPopupVisible: true,
      }));

      // Get listings that have not been inquired/contacted by the user
      const availableSimilarListings = await dispatch(
        FetchListingActions.fetchSimilarListingsFromApi({
          maloneLotIdEncoded: listing.maloneLotIdEncoded,
          unit: listing.unit,
          listingTypes: listing.listingType,
          limit: 5,
          excludeInquiredListings: true,
        }),
      );

      const hasAvailableSimilarListings = availableSimilarListings && availableSimilarListings.length > 0;

      // Chain additional modals after successful contact submission
      if (shouldDisplayPostContactSavedSearch && isLoggedIn && !hasShownPostContactSavedSearch) {
        openModal({
          type: 'postContactSavedSearch',
          onClose: () => {
            // After saved search modal closes, show contact success if available
            if (hasAvailableSimilarListings) {
              openModal({
                type: 'contactSuccess',
                props: { similarListings: availableSimilarListings, listing },
                onClose: () => noop,
              });
            }
          },
        });
      } else if (listing.waitlisted && hasAvailableSimilarListings) {
        openModal({
          type: 'waitlistSuccess',
          props: { similarListings: availableSimilarListings },
          onClose: noop,
        });
      } else if (hasAvailableSimilarListings) {
        openModal({
          type: 'contactSuccess',
          props: { similarListings: availableSimilarListings, listing },
          onClose: noop,
        });
      }

      if (!trusted) {
        dispatch(ConversationActions.optimisticSetSentMessage(aliasEncoded));
      }
    } catch (error) {
      dispatch(
        ErrorActions.errorHandler({
          error,
          errorLocation: 'hook.useContactSubmission.continueContactInquiryFlow',
          errorClass: 'contactActions',
        }),
      );

      handleFormSubmitFailure();
    }
  }, [
    dispatch,
    listing,
    onClose,
    closePopup,
    onHidePopup,
    shouldDisplayPostContactSavedSearch,
    openModal,
    handleFormSubmitFailure,
    isLoggedIn,
    hasShownPostContactSavedSearch,
  ]);

  const handleSubmitForm = useCallback(
    async (inputValues: ContactFormData) => {
      /**
       * DISABLE CONTACT INFO VALIDATION
       * Turns off contact info validation when developing. This should speed
       * things up when devs need to work on CTA related logic.
       *
       * __DEV__ flag should ensure that this never gets enabled in production environments.
       */
      const DISABLE_VALIDATION_FOR_CONTACT_TESTING = true;
      const { isValid } =
        DISABLE_VALIDATION_FOR_CONTACT_TESTING && __DEV__ ? { isValid: true } : await validateUserInfo(inputValues);

      if (isValid) {
        try {
          const rentalSubmitId = uuidv4();

          handleTrackSubmitClick(rentalSubmitId);
          setFormState((prev) => ({
            ...prev,
            isLoading: true,
            emailErrorMessage: '',
            errorMessage: '',
          }));

          await dispatch(AppActions.setAppStoreBool('userExists', false));
          const userDetails = (await dispatch(
            ContactActions.loginUser(user.loggedIn, inputValues.userEmail),
          )) as unknown as UserDetailsResponse;

          if (!userDetails) {
            handleFormSubmitFailure();
            return;
          }

          if (userDetails.error) {
            handleFormSubmitFailure({ message: userDetails.message });
            return;
          }

          if (userDetails.status === 'ACCOUNT_DEACTIVATED') {
            setFormState((prev) => ({
              ...prev,
              isLoading: false,
              emailErrorMessage:
                'This email is associated with an account marked for deletion. Please use another email address.',
            }));

            return;
          }

          if (userDetails.status === 'USER_ALREADY_EXISTS') {
            dispatch(AppActions.setAppStoreBool('userExists', true));
          } else if (
            userDetails.success &&
            userDetails?.data?.passwordStatus?.toLowerCase() === 'freepass' &&
            countryCode &&
            loginUtils.isCanadianUser(countryCode, inputValues.userEmail)
          ) {
            // freepassed user from canada
            dispatch(NotificationActions.email.updateEmailOptIn(true, 'inquiry'));
          }

          // Store contact inquiry details for sending after user closes rental profile form.
          contactUtils.setDelayedContactInquiry({
            listing,
            inquiryParams: getInquiryParams(inputValues),
            rentalSubmitId,
            wasLoggedInAtStart: isLoggedIn,
          });

          // Open renter profile modal first, then continue the contact flow
          openModal({
            type: 'postContactRenterProfile',
            onClose: continueContactInquiryFlow,
          });
        } catch (error) {
          dispatch(
            ErrorActions.errorHandler({
              error,
              errorLocation: 'hook.useContactSubmission.handleSubmitForm',
              errorClass: 'contactActions',
            }),
          );

          handleFormSubmitFailure();
        }
      }
    },
    [
      dispatch,
      user,
      listing,
      validateUserInfo,
      handleTrackSubmitClick,
      handleFormSubmitFailure,
      openModal,
      getInquiryParams,
      continueContactInquiryFlow,
      countryCode,
    ],
  );

  const handlePhoneClick = useCallback(() => {
    const listingType = getListingType(listing);
    const leadType = 'phone';

    // Determine trigger object based on context
    // Note: PhoneLeadTriggerObject only supports form-specific triggers
    const defaultPhoneTriggerObject: PhoneLeadTriggerObject = isPopup
      ? 'home_details_component|modal_contact_form'
      : 'home_details_component|inline_form';

    // Check if triggerObject is valid for phone tracking, otherwise use default
    const isValidPhoneTriggerObject =
      triggerObject === 'home_details_component|modal_contact_form' ||
      triggerObject === 'home_details_component|inline_form';
    const finalPhoneTriggerObject: PhoneLeadTriggerObject = isValidPhoneTriggerObject
      ? (triggerObject as PhoneLeadTriggerObject)
      : defaultPhoneTriggerObject;

    getTrackingFunction({ type: listingType, leadType })?.({
      listing,
      triggerLocation,
      triggerObject: finalPhoneTriggerObject,
    });
  }, [listing, isPopup, triggerLocation, triggerObject]);

  const clearErrors = useCallback(() => {
    setFormState((prev) => ({
      ...prev,
      errorMessage: '',
      emailErrorMessage: '',
      phoneErrorMessage: '',
    }));
  }, []);

  return {
    handleSubmitForm,
    handlePhoneClick,
    formState,
    clearErrors,
  };
};
